import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
const API = "/api";

// B16 — oferty AliExpress. Kontrakt backendu (backend/aliexpress_offers.py):
//   GET  /api/aliexpress-offers?page&status&q        -> {offers,total,page,pages}
//   GET  /api/aliexpress-offers/{product_id}         -> szczegoly + skus[]
//   PATCH /api/aliexpress-offers/{product_id}        -> {ok,updated,errors,product}

type AeOffer = {
  product_id: number;
  subject: string | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
  image: string | null;
  gmt_create: string | null;
  gmt_modified: string | null;
};

type AeSku = {
  sku_id: string;
  sku_code: string | null;
  price: number;
  currency: string | null;
  stock: number;
  ean: string | null;
  active: boolean;
  label: string | null;
  image: string | null;
};

type AeProduct = {
  product_id: number;
  subject: string | null;
  status: string | null;
  price: number | null;
  currency: string | null;
  images: string[];
  image: string | null;
  category_id: number | null;
  gmt_create: string | null;
  gmt_modified: string | null;
  weight_kg: number | null;
  package_lwh: (number | null)[];
  url: string;
  skus: AeSku[];
};

const LIMIT = 50;

const s: { [k: string]: React.CSSProperties } = {
  input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, background: '#fff' },
  btn: { border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer', background: '#fff' },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', flexDirection: 'column', gap: 4 },
};

const auth = () => ({ headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });

const fmtPrice = (v: number | null | undefined, cur?: string | null) =>
  (v == null ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) + (cur ? ' ' + cur : '');

const fmtDT = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const p = (x: number) => String(x).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
};

export default function AliexpressOffers() {
  // --- lista ---
  const [offers, setOffers] = useState<AeOffer[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('onSale');

  // --- szczegoly + edycja ---
  const [det, setDet] = useState<AeProduct | null>(null);
  const [detLoading, setDetLoading] = useState(false);
  const [edit, setEdit] = useState<{ [skuId: string]: { price: string; stock: string } }>({});
  const [saving, setSaving] = useState(false);

  // --- edycja masowa ---
  const [sel, setSel] = useState<{ [pid: string]: AeOffer }>({});
  const selArr = Object.values(sel);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ price: '', stock: '' });
  const [bulkRun, setBulkRun] = useState({ running: false, done: 0, total: 0, current: '' });
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const bulkCancel = useRef(false);

  const load = useCallback(async (p: number) => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(API + '/aliexpress-offers', {
        params: { page: p, page_size: LIMIT, status: statusF, ...(q ? { q } : {}) },
        ...auth(),
      });
      setOffers(r.data?.offers || []);
      setTotal(r.data?.total || 0);
      setPages(Math.max(1, r.data?.pages || 1));
      setPage(p);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'błąd pobierania listy AliExpress');
    } finally {
      setLoading(false);
    }
  }, [q, statusF]);

  useEffect(() => { load(1); }, [q, statusF]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = () => { setQ(searchInput.trim()); };
  const refresh = () => { load(page); setMsg(''); };

  const toggleSel = (o: AeOffer) => {
    setSel(prev => {
      const n = { ...prev };
      if (n[String(o.product_id)]) delete n[String(o.product_id)];
      else n[String(o.product_id)] = o;
      return n;
    });
  };
  const allPageSelected = offers.length > 0 && offers.every(o => sel[String(o.product_id)]);
  const toggleAllPage = () => {
    setSel(prev => {
      const n = { ...prev };
      if (allPageSelected) offers.forEach(o => delete n[String(o.product_id)]);
      else offers.forEach(o => { n[String(o.product_id)] = o; });
      return n;
    });
  };

  // --- szczegoly ---
  const openDetails = async (pid: number) => {
    setDetLoading(true); setDet(null); setEdit({}); setErr('');
    try {
      const r = await axios.get(API + '/aliexpress-offers/' + pid, auth());
      const d: AeProduct = r.data;
      setDet(d);
      const e: { [k: string]: { price: string; stock: string } } = {};
      d.skus.forEach(k => { e[k.sku_id] = { price: String(k.price ?? ''), stock: String(k.stock ?? '') }; });
      setEdit(e);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'błąd pobierania szczegółów');
    } finally {
      setDetLoading(false);
    }
  };

  const changedCount = (): number => {
    if (!det) return 0;
    let n = 0;
    det.skus.forEach(k => {
      const e = edit[k.sku_id];
      if (!e) return;
      const p = parseFloat(String(e.price).replace(',', '.'));
      const st = parseInt(String(e.stock), 10);
      if (!isNaN(p) && Math.abs(p - (k.price ?? 0)) > 0.000001) n++;
      else if (!isNaN(st) && st !== (k.stock ?? 0)) n++;
      else if (String(e.price).trim() === '' || String(e.stock).trim() === '') n++;
    });
    return n;
  };

  const saveDetails = async () => {
    if (!det) return;
    const skus: any[] = [];
    det.skus.forEach(k => {
      const e = edit[k.sku_id];
      if (!e) return;
      const p = parseFloat(String(e.price).replace(',', '.'));
      const st = parseInt(String(e.stock), 10);
      const item: any = { sku_id: k.sku_id, sku_code: k.sku_code || undefined };
      let touched = false;
      if (!isNaN(p) && Math.abs(p - (k.price ?? 0)) > 0.000001) { item.price = p; touched = true; }
      if (!isNaN(st) && st !== (k.stock ?? 0)) { item.inventory = st; touched = true; }
      if (touched) skus.push(item);
    });
    if (!skus.length) { setMsg('Brak zmian do zapisania.'); return; }
    setSaving(true); setErr(''); setMsg('Zapisuję…');
    try {
      const r = await axios.patch(API + '/aliexpress-offers/' + det.product_id, { skus }, auth());
      if (r.data?.product) {
        const d: AeProduct = r.data.product;
        setDet(d);
        const e: { [k: string]: { price: string; stock: string } } = {};
        d.skus.forEach(k => { e[k.sku_id] = { price: String(k.price ?? ''), stock: String(k.stock ?? '') }; });
        setEdit(e);
      }
      const up = (r.data?.updated || []).length;
      const er = (r.data?.errors || []);
      setMsg('✔ Zapisano zmiany wariantów: ' + up + (er.length ? ' (błędy: ' + er.length + ')' : ''));
      if (er.length) setErr(er.join('; '));
      load(page);
    } catch (e: any) {
      setMsg('');
      setErr(e?.response?.data?.detail || 'błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  // --- edycja masowa: ustaw cene/stan na WSZYSTKIE warianty zaznaczonych produktow ---
  const runBulk = async () => {
    const targets = selArr;
    if (!targets.length) return;
    const price = bulkForm.price.trim() === '' ? null : parseFloat(bulkForm.price.replace(',', '.'));
    const stock = bulkForm.stock.trim() === '' ? null : parseInt(bulkForm.stock, 10);
    if (price === null && stock === null) { setBulkLog(['Podaj cenę i/lub stan.']); return; }
    if (price !== null && (isNaN(price) || price <= 0)) { setBulkLog(['Niepoprawna cena.']); return; }
    if (stock !== null && (isNaN(stock) || stock < 0)) { setBulkLog(['Niepoprawny stan.']); return; }
    bulkCancel.current = false;
    setBulkRun({ running: true, done: 0, total: targets.length, current: '' });
    setBulkLog([]);
    const log: string[] = [];
    let ok = 0, fail = 0;
    for (let i = 0; i < targets.length; i++) {
      if (bulkCancel.current) { log.push('⏹ Przerwano po ' + i + ' z ' + targets.length); break; }
      const o = targets[i];
      setBulkRun(r => ({ ...r, done: i, current: o.subject || String(o.product_id) }));
      try {
        const d = await axios.get(API + '/aliexpress-offers/' + o.product_id, auth());
        const skus = ((d.data?.skus) || []).map((k: any) => ({
          sku_id: k.sku_id, sku_code: k.sku_code || undefined,
          ...(price !== null ? { price } : {}), ...(stock !== null ? { inventory: stock } : {}),
        }));
        if (!skus.length) { fail++; log.push('✖ ' + (o.subject || o.product_id) + ': brak wariantów'); continue; }
        await axios.patch(API + '/aliexpress-offers/' + o.product_id, { skus }, auth());
        ok++; log.push('✔ ' + (o.subject || o.product_id));
      } catch (e: any) {
        fail++; log.push('✖ ' + (o.subject || o.product_id) + ': ' + (e?.response?.data?.detail || 'błąd'));
      }
      await new Promise<void>(res => setTimeout(res, 300));
    }
    setBulkRun(r => ({ ...r, running: false, done: targets.length, current: '' }));
    setBulkLog(['Zapisane: ' + ok + ' / ' + targets.length + (fail ? ', błędy: ' + fail : ''), '— szczegóły —', ...log]);
    setSel({});
    load(page);
  };

  const priceCell = (o: AeOffer) => {
    if (o.price_min == null && o.price_max == null) return '—';
    if (o.price_min != null && o.price_max != null && Math.abs(o.price_min - o.price_max) > 0.000001)
      return fmtPrice(o.price_min) + ' – ' + fmtPrice(o.price_max) + ' ' + (o.currency || '');
    return fmtPrice(o.price_min != null ? o.price_min : o.price_max, o.currency);
  };

  return (
    <div>
      {/* naglowek + filtry */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, flex: '1 1 280px' }}>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="Szukaj po nazwie (przeszukiwane po stronie AliExpress)…"
              style={{ ...s.input, flex: 1 }} />
            <button onClick={doSearch} style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>Szukaj</button>
          </div>
          <select value={statusF} onChange={e => { setStatusF(e.target.value); setSel({}); }} style={s.input}>
            <option value="onSale">Aktywne (onSale)</option>
            <option value="offline">Nieaktywne (offline)</option>
          </select>
          <button onClick={refresh} disabled={loading} style={s.btnGhost}>{loading ? '…' : '↻ Odśwież'}</button>
          {selArr.length > 0 && (
            <button onClick={() => { setBulkOpen(true); setBulkLog([]); }}
              style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>
              Edycja masowa ({selArr.length})
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
          {total} produktów · strona {page} z {pages}{q ? ' · filtr: "' + q + '"' : ''} · nazwy na liście po angielsku, w szczegółach po polsku (tak zwraca API AliExpress)
        </div>
        {err && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>{err}</div>}
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, padding: '6px 10px' }}>{msg}</div>}
      </div>

      {/* tabela */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 34 }}>
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} title="Zaznacz / odznacz wszystkie na stronie" />
              </th>
              <th style={s.th}>Nazwa (EN) / ID produktu</th>
              <th style={s.th}>Cena</th>
              <th style={s.th}>Zmodyfikowano</th>
              <th style={{ ...s.th, width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {offers.map(o => (
              <tr key={o.product_id} style={{ ...(sel[String(o.product_id)] ? { background: '#eff6ff' } : {}) }}>
                <td style={s.td}>
                  <input type="checkbox" checked={!!sel[String(o.product_id)]} onChange={() => toggleSel(o)} />
                </td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {o.image ? (
                        <img src={o.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={e => { const t = e.target as HTMLImageElement; if (t && t.style) t.style.visibility = 'hidden'; }} />
                      ) : (
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>brak</span>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, maxWidth: 420 }}>{o.subject || '—'}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>{o.product_id}</div>
                    </div>
                  </div>
                </td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{priceCell(o)}</td>
                <td style={{ ...s.td, fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDT(o.gmt_modified)}</td>
                <td style={s.td}>
                  <button onClick={() => openDetails(o.product_id)} style={{ ...s.btnGhost, fontSize: 12, padding: '5px 10px' }}>Szczegóły</button>
                </td>
              </tr>
            ))}
            {!loading && offers.length === 0 && (
              <tr><td style={{ ...s.td, color: '#6b7280', textAlign: 'center', padding: 24 }} colSpan={5}>Brak produktów{q ? ' dla zapytania "' + q + '"' : ''}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* paginacja */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', margin: '12px 0' }}>
        <button onClick={() => load(page - 1)} disabled={page <= 1 || loading} style={s.btnGhost}>‹ Poprzednia</button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Strona {page} / {pages}</span>
        <button onClick={() => load(page + 1)} disabled={page >= pages || loading} style={s.btnGhost}>Następna ›</button>
      </div>

      {/* modal: szczegoly + edycja */}
      {(det || detLoading) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
          onClick={() => { if (!saving) { setDet(null); setDetLoading(false); } }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 940, width: '100%', padding: 18 }}
            onClick={e => e.stopPropagation()}>
            {detLoading || !det ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Pobieram szczegóły z AliExpress…</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{det.subject || '(bez nazwy)'}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      ID {det.product_id} · status: {det.status || '—'} · kategoria: {det.category_id || '—'} ·
                      {' '}zmodyfikowano: {fmtDT(det.gmt_modified)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={det.url} target="_blank" rel="noreferrer" style={{ ...s.btnGhost, textDecoration: 'none', display: 'inline-block' }}>Otwórz na AliExpress ↗</a>
                    <button onClick={() => { setDet(null); setDetLoading(false); }} disabled={saving} style={s.btnGhost}>✕</button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' as const, fontSize: 12, color: '#374151', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div><b>Cena bazowa:</b> {fmtPrice(det.price, det.currency)}</div>
                  <div><b>Waga:</b> {det.weight_kg != null ? det.weight_kg + ' kg' : '—'}</div>
                  <div><b>Opakowanie (D×S×W):</b> {det.package_lwh && det.package_lwh[0] != null ? det.package_lwh.join(' × ') + ' cm' : '—'}</div>
                  <div><b>Warianty:</b> {det.skus.length}</div>
                </div>

                {det.images.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, paddingBottom: 4 }}>
                    {det.images.map((u, i) => (
                      <img key={i} src={u} alt="" loading="lazy" style={{ width: 56, height: 56, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, flexShrink: 0, background: '#f9fafb' }} />
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 13, fontWeight: 600, margin: '6px 0' }}>Warianty (SKU) — edycja ceny i stanu:</div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={s.th}>Wariant</th>
                        <th style={s.th}>SKU / EAN</th>
                        <th style={{ ...s.th, width: 110 }}>Cena</th>
                        <th style={{ ...s.th, width: 90 }}>Stan</th>
                        <th style={{ ...s.th, width: 70 }}>Aktywny</th>
                      </tr>
                    </thead>
                    <tbody>
                      {det.skus.map(k => (
                        <tr key={k.sku_id}>
                          <td style={s.td}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              {k.image && (
                                <img src={k.image} alt="" loading="lazy" style={{ width: 34, height: 34, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb' }} />
                              )}
                              <div>
                                <div>{k.label || k.sku_id}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{k.sku_id}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>
                            {k.sku_code || '—'}{k.ean ? <div style={{ color: '#6b7280' }}>{k.ean}</div> : null}
                          </td>
                          <td style={s.td}>
                            <input value={edit[k.sku_id]?.price ?? ''} disabled={saving}
                              onChange={e => setEdit(prev => ({ ...prev, [k.sku_id]: { ...prev[k.sku_id], price: e.target.value } }))}
                              inputMode="decimal" style={{ ...s.input, width: 90, textAlign: 'right' }} />
                          </td>
                          <td style={s.td}>
                            <input value={edit[k.sku_id]?.stock ?? ''} disabled={saving}
                              onChange={e => setEdit(prev => ({ ...prev, [k.sku_id]: { ...prev[k.sku_id], stock: e.target.value } }))}
                              inputMode="numeric" style={{ ...s.input, width: 70, textAlign: 'right' }} />
                          </td>
                          <td style={{ ...s.td, color: k.active ? '#065f46' : '#b91c1c' }}>{k.active ? 'tak' : 'nie'}</td>
                        </tr>
                      ))}
                      {det.skus.length === 0 && (
                        <tr><td style={{ ...s.td, color: '#6b7280', textAlign: 'center', padding: 18 }} colSpan={5}>Produkt bez wariantów.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>zmian: {changedCount()}</span>
                  <button onClick={() => { setDet(null); setDetLoading(false); }} disabled={saving} style={s.btnGhost}>Zamknij</button>
                  <button onClick={saveDetails} disabled={saving || changedCount() === 0}
                    style={{ ...s.btn, background: '#1d4ed8', color: '#fff', opacity: (saving || changedCount() === 0) ? 0.6 : 1 }}>
                    {saving ? 'Zapisuję…' : 'Zapisz zmiany'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* modal: edycja masowa */}
      {bulkOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 560, width: '100%', padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Edycja masowa — {selArr.length} produkt(ów)</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Ustawioną cenę/stan aplikuję do <b>wszystkich wariantów</b> każdego zaznaczonego produktu. Puste pole = bez zmian.
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <label style={s.label}>Cena ({selArr[0]?.currency || 'PLN'})
                <input value={bulkForm.price} disabled={bulkRun.running} inputMode="decimal"
                  onChange={e => setBulkForm(f => ({ ...f, price: e.target.value }))} style={s.input} placeholder="np. 129.90" />
              </label>
              <label style={s.label}>Stan
                <input value={bulkForm.stock} disabled={bulkRun.running} inputMode="numeric"
                  onChange={e => setBulkForm(f => ({ ...f, stock: e.target.value }))} style={s.input} placeholder="np. 100" />
              </label>
            </div>
            {bulkRun.running && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: Math.round((bulkRun.done / Math.max(1, bulkRun.total)) * 100) + '%', height: '100%', background: '#1d4ed8', transition: 'width .2s' }} />
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {bulkRun.done}/{bulkRun.total} · {bulkRun.current}
                </div>
              </div>
            )}
            {bulkLog.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12, background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 6, padding: 8, marginBottom: 10 }}>
                {bulkLog.map((l, i) => <div key={i} style={{ whiteSpace: 'normal' }}>{l}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {bulkRun.running ? (
                <button onClick={() => { bulkCancel.current = true; }} style={{ ...s.btn, background: '#b91c1c', color: '#fff' }}>Przerwij</button>
              ) : (
                <>
                  <button onClick={() => setBulkOpen(false)} style={s.btnGhost}>Zamknij</button>
                  <button onClick={runBulk} style={{ ...s.btn, background: '#1d4ed8', color: '#fff' }}>Zastosuj do {selArr.length}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
